import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { webApi } from '../services/api';
import { isCanceledRequest } from '../utils/abortRequest';
import { formatNotificationCardTimestamp } from '../utils/dateDisplay';

function isUnreadNotification(item) {
  if (item && typeof item.isRead === 'boolean') return !item.isRead;
  return !item?.readAt;
}

function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const load = async (signal) => {
    const axiosOpts = signal ? { signal } : {};
    try {
      const [list, unread] = await Promise.all([
        webApi.getNotifications(20, 0, axiosOpts),
        webApi.getUnreadCount(axiosOpts)
      ]);
      if (signal?.aborted) return;
      setNotifications(list?.notifications || []);
      setUnreadCount(Number(unread?.unreadCount || 0));
      setMessage('');
    } catch (err) {
      if (signal?.aborted || isCanceledRequest(err)) return;
      setMessage(err.message || 'Failed to load notifications');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, []);

  const markOne = async (id) => {
    const item = notifications.find((n) => n.id === id);
    if (item && !isUnreadNotification(item)) return;
    try {
      await webApi.markNotificationRead(id);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, isRead: true, readAt: n.readAt || new Date().toISOString() } : n
        )
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      setMessage(err.message || 'Failed to mark notification');
    }
  };

  const markAll = async () => {
    try {
      await webApi.markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      setMessage(err.message || 'Failed to mark all notifications');
    }
  };

  return (
    <div className="ph-page">
      <header className="ph-topbar">
        <button type="button" className="ph-back" onClick={() => navigate(-1)} aria-label="Go back">
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </button>
        <h1 className="ph-topbar-title">Notification</h1>
        {unreadCount > 0 ? (
          <button type="button" className="notif-mark-all" onClick={markAll}>
            Mark all read
          </button>
        ) : (
          <span className="ph-topbar-spacer" aria-hidden="true" />
        )}
      </header>

      <div className="ph-body">
        {message ? (
          <p className="ph-banner ph-banner--error" role="alert">
            {message}
          </p>
        ) : null}

        {loading ? (
          <p className="ph-loading">Loading…</p>
        ) : notifications.length === 0 ? (
          <div className="ph-empty">
            <p className="ph-empty-title">No notifications</p>
            <p className="ph-empty-sub">You&apos;re all caught up.</p>
          </div>
        ) : (
          <ul className="notif-list">
            {notifications.map((item) => {
              const unread = isUnreadNotification(item);
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`notif-card${unread ? ' notif-card--unread' : ''}`}
                    onClick={() => markOne(item.id)}
                  >
                    {unread ? <span className="notif-card-accent" aria-hidden="true" /> : null}
                    <div className="notif-card-inner">
                      <p className="notif-card-title">{item.title || item.category || 'Notification'}</p>
                      <p className="notif-card-body">{item.body || item.message || '—'}</p>
                      <p className="notif-card-time">{formatNotificationCardTimestamp(item.createdAt)}</p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default NotificationsPage;
